import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { loadStats } from '../lib/userStats';
import { matchSchools } from '../data/schools';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const C = {
  bg:      '#f8fafc',
  surface: '#ffffff',
  border:  '#e5e7eb',
  blue:    '#3b82f6',
  blueDk:  '#2563eb',
  indigo:  '#6366f1',
  amber:   '#f59e0b',
  red:     '#ef4444',
  text:    '#0f172a',
  textSec: '#64748b',
  textMut: '#94a3b8',
};

const CAT = {
  safety:   { label: 'Target',    color: '#fff', bg: '#16a34a', border: '#16a34a', scoreColor: '#16a34a' },
  match:    { label: 'Match',     color: '#fff', bg: '#3b82f6', border: '#3b82f6', scoreColor: '#3b82f6' },
  reach:    { label: 'Reach',     color: '#fff', bg: '#f59e0b', border: '#f59e0b', scoreColor: '#d97706' },
  longshot: { label: 'Long Shot', color: '#fff', bg: '#ef4444', border: '#ef4444', scoreColor: '#ef4444' },
};

const DEGREE_OPTIONS = ['MD', 'DO'];
const REGION_OPTIONS = ['US', 'Canada', 'Caribbean', 'Puerto Rico'];
const CAT_PILLS = [
  { label: 'All',       value: 'all'      },
  { label: 'Target',    value: 'safety'   },
  { label: 'Match',     value: 'match'    },
  { label: 'Reach',     value: 'reach'    },
  { label: 'Long Shot', value: 'longshot' },
];

const TOUR_STEPS = [
  { target: 'stats',   title: 'Your stats',     desc: 'Your MCAT, GPA, and state drive every match result. Edit them on the Dashboard and results update instantly.' },
  { target: 'filters', title: 'Filter schools', desc: 'Narrow results by degree type, region, or category. Your filter selections are saved automatically.' },
  { target: 'grid',    title: 'Your matches',   desc: 'Schools are ranked as Target, Match, Reach, or Long Shot based on how your stats compare to admitted students.' },
  { target: 'grid',    title: 'Save schools',   desc: 'Click the Save button on any card to add a school to your list. Saved schools show up in Build My List.' },
];

function lsFiltersKey(uid) { return 'previsr_filters_' + uid; }

function loadFilters(uid) {
  try {
    const raw = localStorage.getItem(lsFiltersKey(uid));
    if (raw) {
      const p = JSON.parse(raw);
      return {
        degreeTypes: p.degreeTypes?.length ? p.degreeTypes : DEGREE_OPTIONS.slice(),
        regions:     p.regions?.length     ? p.regions     : REGION_OPTIONS.slice(),
      };
    }
  } catch (_) {}
  return { degreeTypes: DEGREE_OPTIONS.slice(), regions: REGION_OPTIONS.slice() };
}

function saveFilters(uid, f) {
  try { localStorage.setItem(lsFiltersKey(uid), JSON.stringify(f)); } catch (_) {}
}

function CategoryBadge({ category }) {
  const s = CAT[category];
  if (!s) return null;
  return (
    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', padding: '2px 7px', borderRadius: 3, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
}

export default function SchoolMatcher({ session }) {
  const isMobile = useIsMobile();
  const [loading, setLoading]         = useState(true);
  const [userStats, setUserStats]     = useState({ mcat: '', gpa: '', sciGpa: '', state: '' });
  const [filters, setFilters]         = useState({ degreeTypes: DEGREE_OPTIONS.slice(), regions: REGION_OPTIONS.slice() });
  const [catFilter, setCatFilter]     = useState('all');
  const [results, setResults]         = useState([]);
  const [savedIds, setSavedIds]       = useState(new Set());
  const [savingId, setSavingId]       = useState(null);
  const [tourStep, setTourStep]       = useState(null);

  const uid = session?.user?.id || null;

  const runMatch = useCallback(function(s, f) {
    const mcat = Number(s.mcat);
    const gpa  = Number(s.gpa);
    if (!mcat || !gpa || !s.state) { setResults([]); return; }
    setResults(matchSchools({
      mcat, gpa,
      sciGpa:     s.sciGpa ? Number(s.sciGpa) : null,
      state:      s.state,
      degreeTypes: f.degreeTypes.length ? f.degreeTypes : DEGREE_OPTIONS.slice(),
      regions:    f.regions.length     ? f.regions     : REGION_OPTIONS.slice(),
    }));
  }, []);

  useEffect(function() {
    if (!uid) { setLoading(false); return; }
    try { if (!localStorage.getItem('previsr_matcher_tour_complete_' + uid)) setTourStep(0); } catch (_) {}
    const savedFilt = loadFilters(uid);
    setFilters(savedFilt);

    Promise.all([
      loadStats(uid),
      supabase.from('saved_schools').select('school_id').eq('user_id', uid),
    ]).then(function([stats, savedRes]) {
      setUserStats(stats);
      if (!savedRes.error && savedRes.data) {
        setSavedIds(new Set(savedRes.data.map(function(r) { return r.school_id; })));
      }
      runMatch(stats, savedFilt);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }, [uid, runMatch]);

  function toggleFilter(group, value) {
    const next = Object.assign({}, filters, { [group]: filters[group].slice() });
    const idx  = next[group].indexOf(value);
    if (idx >= 0) {
      if (next[group].length === 1) return;
      next[group].splice(idx, 1);
    } else {
      next[group].push(value);
    }
    setFilters(next);
    if (uid) saveFilters(uid, next);
    runMatch(userStats, next);
  }

  async function toggleSave(school) {
    if (!uid || savingId) return;
    setSavingId(school.id);
    const isSaved = savedIds.has(school.id);
    if (isSaved) {
      const next = new Set(savedIds); next.delete(school.id); setSavedIds(next);
      const { error } = await supabase.from('saved_schools').delete().eq('user_id', uid).eq('school_id', school.id);
      if (error) setSavedIds(new Set(savedIds));
    } else {
      const next = new Set(savedIds); next.add(school.id); setSavedIds(next);
      const { error } = await supabase.from('saved_schools').insert({ user_id: uid, school_id: school.id, school_name: school.name, category: school.category });
      if (error) { const rb = new Set(savedIds); rb.delete(school.id); setSavedIds(rb); }
    }
    setSavingId(null);
  }

  function dismissTour() {
    setTourStep(null);
    try { if (uid) localStorage.setItem('previsr_matcher_tour_complete_' + uid, '1'); } catch (_) {}
  }
  function advanceTour() {
    if (tourStep >= TOUR_STEPS.length - 1) dismissTour();
    else setTourStep(function(s) { return s + 1; });
  }
  function tourTarget(tgt) {
    if (tourStep === null || TOUR_STEPS[tourStep]?.target !== tgt) return {};
    return { position: 'relative', zIndex: 201, boxShadow: '0 0 0 3px #3b82f6, 0 0 20px rgba(59,130,246,0.25)', borderRadius: 10 };
  }
  function tourTargetFull(tgt) {
    if (tourStep === null || TOUR_STEPS[tourStep]?.target !== tgt) return {};
    return { position: 'relative', zIndex: 201, boxShadow: '0 0 0 3px #3b82f6, 0 0 20px rgba(59,130,246,0.25)' };
  }

  const hasStats = !!(userStats.mcat && userStats.gpa && userStats.state);

  const filteredResults = catFilter === 'all'
    ? results
    : results.filter(function(s) { return s.category === catFilter; });

  const catCounts = {
    safety:   results.filter(function(s) { return s.category === 'safety';   }).length,
    match:    results.filter(function(s) { return s.category === 'match';    }).length,
    reach:    results.filter(function(s) { return s.category === 'reach';    }).length,
    longshot: results.filter(function(s) { return s.category === 'longshot'; }).length,
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
        <div style={{ width: 24, height: 24, border: `3px solid ${C.border}`, borderTopColor: C.blue, borderRadius: '50%', animation: 'spin .7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: C.bg, minHeight: '100vh' }}>

      {/* ── Tour overlay ─────────────────────────────────────────────────────── */}
      {tourStep !== null && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.52)', zIndex: 200, pointerEvents: 'none' }} />
          <div style={{ position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', zIndex: 202, background: C.surface, borderRadius: 14, padding: '22px 26px', width: isMobile ? 'calc(100vw - 32px)' : 380, boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMut, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 8 }}>
              Step {tourStep + 1} of {TOUR_STEPS.length}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>
              {TOUR_STEPS[tourStep].title}
            </div>
            <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.65, marginBottom: 20 }}>
              {TOUR_STEPS[tourStep].desc}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button onClick={dismissTour} style={{ background: 'none', border: 'none', fontSize: 12, color: C.textMut, cursor: 'pointer', padding: 0 }}>
                Skip tour
              </button>
              <button onClick={advanceTour} style={{ background: C.blue, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {tourStep === TOUR_STEPS.length - 1 ? 'Done' : 'Next'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 16 }}>
              {TOUR_STEPS.map(function(_, i) {
                return <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: i === tourStep ? C.blue : C.border }} />;
              })}
            </div>
          </div>
        </>
      )}

      {/* Topbar */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '14px 16px' : '14px 24px', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.text, letterSpacing: '-0.3px' }}>School Matcher</div>
        <div style={{ fontSize: 11, color: C.textMut, marginTop: 2 }}>Explore and save schools based on your stats</div>
      </div>

      {/* Stats display */}
      {hasStats && (
        <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '8px 16px' : '8px 24px', display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 20, flexWrap: 'wrap', ...tourTargetFull('stats') }}>
          {[
            { label: 'MCAT',    value: userStats.mcat },
            { label: 'GPA',     value: userStats.gpa    ? Number(userStats.gpa).toFixed(2)    : '' },
            { label: 'Sci GPA', value: userStats.sciGpa ? Number(userStats.sciGpa).toFixed(2) : '' },
            { label: 'State',   value: userStats.state },
          ].filter(function(i) { return i.value; }).map(function(item) {
            return (
              <div key={item.label} style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: C.textMut }}>{item.label}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{item.value}</span>
              </div>
            );
          })}
          <Link to="/dashboard" style={{ marginLeft: 'auto', fontSize: 11, color: C.blue, textDecoration: 'none', fontWeight: 600 }}>
            Edit on Dashboard
          </Link>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: isMobile ? '10px 16px' : '10px 24px', display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 20, flexWrap: 'wrap', ...tourTargetFull('filters') }}>
        {/* Degree */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: C.textMut, marginRight: 2 }}>Degree</span>
          {DEGREE_OPTIONS.map(function(opt) {
            const active = filters.degreeTypes.includes(opt);
            return (
              <button key={opt} onClick={function() { toggleFilter('degreeTypes', opt); }}
                style={{ padding: '4px 11px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? C.blue : C.border}`, background: active ? C.blue : C.surface, color: active ? '#fff' : C.textSec, transition: 'all .12s' }}>
                {opt}
              </button>
            );
          })}
        </div>
        <div style={{ width: 1, height: 20, background: C.border }} />
        {/* Region */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: C.textMut, marginRight: 2 }}>Region</span>
          {REGION_OPTIONS.map(function(opt) {
            const active = filters.regions.includes(opt);
            return (
              <button key={opt} onClick={function() { toggleFilter('regions', opt); }}
                style={{ padding: '4px 11px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? C.blue : C.border}`, background: active ? C.blue : C.surface, color: active ? '#fff' : C.textSec, transition: 'all .12s' }}>
                {opt}
              </button>
            );
          })}
        </div>
        <div style={{ width: 1, height: 20, background: C.border }} />
        {/* Category */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: C.textMut, marginRight: 2 }}>Category</span>
          {CAT_PILLS.map(function(pill) {
            const active = catFilter === pill.value;
            const cnt    = pill.value === 'all' ? results.length : (catCounts[pill.value] || 0);
            const cs     = pill.value !== 'all' ? CAT[pill.value] : null;
            return (
              <button key={pill.value} onClick={function() { setCatFilter(pill.value); }}
                style={{ padding: '4px 11px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active && cs ? cs.border : active ? C.text : C.border}`, background: active && cs ? cs.bg : active ? C.text : C.surface, color: active ? '#fff' : C.textSec, transition: 'all .12s' }}>
                {pill.label}{hasStats ? ` (${cnt})` : ''}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: isMobile ? '16px' : '20px 24px', flex: 1 }}>
        {!hasStats ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, paddingTop: '5rem' }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke={C.blue} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>Enter your stats to see matches</div>
              <div style={{ fontSize: 13, color: C.textSec, maxWidth: 360 }}>Add your MCAT, GPA, and state on the Dashboard to see how you compare at each school.</div>
            </div>
            <Link to="/dashboard" style={{ padding: '9px 20px', background: C.blue, color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
              Go to Dashboard
            </Link>
          </div>
        ) : filteredResults.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, paddingTop: '5rem' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>No schools match the current filters</div>
            <div style={{ fontSize: 12, color: C.textSec }}>Try adjusting your degree type, region, or category filter.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 12, ...tourTarget('grid') }}>
            {filteredResults.map(function(school) {
              const isSaved = savedIds.has(school.id);
              const cs      = CAT[school.category];
              return (
                <div key={school.id}
                  onMouseEnter={function(e) { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(59,130,246,0.1)'; }}
                  onMouseLeave={function(e) { e.currentTarget.style.borderColor = '#dbeafe'; e.currentTarget.style.boxShadow = 'none'; }}
                  style={{ background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, transition: 'border-color .12s, box-shadow .12s' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ marginBottom: 5 }}>
                        <CategoryBadge category={school.category} />
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>{school.name}</div>
                      <div style={{ fontSize: 11, color: C.textMut, marginTop: 2 }}>{school.city}, {school.state}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'flex-start' }}>
                      <button
                        onClick={function() { toggleSave(school); }}
                        disabled={!!savingId}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 7, border: `1px solid ${isSaved ? C.blue : C.border}`, background: isSaved ? C.blue : '#fff', color: isSaved ? '#fff' : C.textSec, fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'all .12s' }}>
                        <svg width="12" height="12" fill={isSaved ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                        </svg>
                        {isSaved ? 'Saved' : 'Save'}
                      </button>
                      <Link to={`/school/${school.id}`}
                        style={{ display: 'flex', alignItems: 'center', padding: '6px 11px', borderRadius: 7, border: `1px solid ${C.border}`, background: '#fff', color: C.textSec, fontSize: 11, fontWeight: 600, textDecoration: 'none' }}>
                        Details
                      </Link>
                    </div>
                  </div>

                  {/* Stats row */}
                  <div style={{ display: 'flex', background: '#fff', border: `1px solid #dbeafe`, borderRadius: 7, overflow: 'hidden' }}>
                    {[
                      { label: 'MCAT',   value: school.mcat?.median ?? '--' },
                      { label: 'GPA',    value: school.gpa?.median != null ? school.gpa.median.toFixed(2) : '--' },
                      { label: 'My pct', value: school.mcatPercentile != null ? `p${school.mcatPercentile}` : '--' },
                    ].map(function(stat, i) {
                      return (
                        <div key={stat.label} style={{ flex: 1, padding: '7px 10px', textAlign: 'center', borderLeft: i > 0 ? '1px solid #dbeafe' : 'none' }}>
                          <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: C.textMut, marginBottom: 2 }}>{stat.label}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: i === 2 && cs ? cs.scoreColor : C.text }}>{stat.value}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
